import { useEffect, useState } from 'react';
import Button from '../../../shared/components/Button';
import {
  useSalesAssumptions,
  useSalesTermsTemplate,
  useUpdateSalesAssumptions,
  useUpdateSalesTermsTemplate,
} from '../hooks/useSales';
import type { SalesInvoiceTermsSection, UpdateSalesPricingAssumptionsPayload } from '../types';

/**
 * Percent fields are FRACTIONS 0..1 on the wire (backend @Min(0) @Max(1)) but are
 * edited as whole percents — the conversion lives here so nothing upstream has to
 * remember it.
 */
const PERCENT_FIELDS: { key: keyof UpdateSalesPricingAssumptionsPayload; label: string }[] = [
  { key: 'minMarginLowPercentage', label: 'Minimum margin — low impact' },
  { key: 'idealMarginLowPercentage', label: 'Ideal margin — low impact' },
  { key: 'minMarginMediumPercentage', label: 'Minimum margin — medium impact' },
  { key: 'idealMarginMediumPercentage', label: 'Ideal margin — medium impact' },
  { key: 'minMarginHighPercentage', label: 'Minimum margin — high impact' },
  { key: 'idealMarginHighPercentage', label: 'Ideal margin — high impact' },
  { key: 'standardCommissionPercentage', label: 'Standard commission on sales' },
  { key: 'platformFeeMarginPercentage', label: 'Platform fee margin' },
];

function AssumptionsSection({ canManage }: { canManage: boolean }) {
  const { data, isLoading, error } = useSalesAssumptions();
  const update = useUpdateSalesAssumptions();
  const [draft, setDraft] = useState<UpdateSalesPricingAssumptionsPayload>({});

  // Seed the draft once the server value arrives (and whenever it changes).
  useEffect(() => {
    if (!data) return;
    setDraft({
      exchangeRateNgnPerUsd: data.exchangeRateNgnPerUsd,
      hourlySupportRateMinor: data.hourlySupportRateMinor,
      minMarginLowPercentage: data.minMarginLowPercentage,
      idealMarginLowPercentage: data.idealMarginLowPercentage,
      minMarginMediumPercentage: data.minMarginMediumPercentage,
      idealMarginMediumPercentage: data.idealMarginMediumPercentage,
      minMarginHighPercentage: data.minMarginHighPercentage,
      idealMarginHighPercentage: data.idealMarginHighPercentage,
      standardCommissionPercentage: data.standardCommissionPercentage,
      platformFeeMarginPercentage: data.platformFeeMarginPercentage,
    });
  }, [data]);

  if (isLoading) return <div className="text-muted-foreground text-sm">Loading assumptions…</div>;
  if (error) return <div className="text-red-500 text-sm">Failed to load pricing assumptions.</div>;

  const setNum = (key: keyof UpdateSalesPricingAssumptionsPayload, value: number) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <section className="bg-card rounded-2xl border border-border p-5">
      <h2 className="text-[15px] font-semibold text-foreground">Pricing assumptions</h2>
      <p className="text-[13px] text-muted-foreground mt-1">
        The inputs every quote is computed from. Changing these does not re-price quotes that
        are already saved — those carry a snapshot.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[13px] font-medium text-foreground">Exchange rate (₦ per $)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            disabled={!canManage}
            value={draft.exchangeRateNgnPerUsd ?? 0}
            onChange={(e) => setNum('exchangeRateNgnPerUsd', Math.max(0, Number(e.target.value) || 0))}
            className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm disabled:opacity-60"
          />
        </label>

        <label className="block">
          <span className="text-[13px] font-medium text-foreground">Hourly support rate (₦)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            disabled={!canManage}
            value={(draft.hourlySupportRateMinor ?? 0) / 100}
            onChange={(e) =>
              setNum('hourlySupportRateMinor', Math.max(0, Math.round((Number(e.target.value) || 0) * 100)))
            }
            className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm disabled:opacity-60"
          />
        </label>

        {PERCENT_FIELDS.map(({ key, label }) => {
          const fraction = (draft[key] as number | undefined) ?? 0;
          return (
            <label key={key} className="block">
              <span className="text-[13px] font-medium text-foreground">{label}</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  disabled={!canManage}
                  value={Math.round(fraction * 1000) / 10}
                  onChange={(e) => {
                    const pct = Number(e.target.value);
                    if (Number.isNaN(pct)) return;
                    setNum(key, Math.min(100, Math.max(0, pct)) / 100);
                  }}
                  className="w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm disabled:opacity-60"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </label>
          );
        })}
      </div>

      {canManage && (
        <div className="mt-5 flex justify-end">
          <Button
            className="h-[40px]"
            loading={update.isPending}
            disabled={update.isPending}
            onClick={() => update.mutate(draft)}
          >
            Save assumptions
          </Button>
        </div>
      )}
    </section>
  );
}

function TermsSection({ canManage }: { canManage: boolean }) {
  const { data, isLoading, error } = useSalesTermsTemplate();
  const update = useUpdateSalesTermsTemplate();
  const [sections, setSections] = useState<SalesInvoiceTermsSection[]>([]);

  useEffect(() => {
    if (data?.sections) setSections(data.sections);
  }, [data]);

  if (isLoading) return <div className="text-muted-foreground text-sm">Loading terms…</div>;
  if (error) return <div className="text-red-500 text-sm">Failed to load the terms template.</div>;

  const setField = (i: number, key: keyof SalesInvoiceTermsSection, value: string) =>
    setSections((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));

  const addSection = () =>
    setSections((prev) => {
      // Derive from the highest existing suffix, not the array length: after a
      // removal, length no longer tracks the max and would regenerate a key that
      // already exists (e.g. [1,2] → remove 1 → add → "SECTION_2" twice).
      const maxSuffix = prev.reduce((max, s) => {
        const n = Number.parseInt(s.key.replace(/^SECTION_/, ''), 10);
        return Number.isNaN(n) ? max : Math.max(max, n);
      }, 0);
      return [...prev, { key: `SECTION_${maxSuffix + 1}`, title: '', body: '' }];
    });

  const removeSection = (i: number) => setSections((prev) => prev.filter((_, idx) => idx !== i));

  // Backend enforces ArrayMinSize(1) and non-empty key/title/body on every section.
  const keys = sections.map((s) => s.key.trim());
  // Keys are identifiers, and the field is hand-editable, so guard uniqueness here
  // too — the backend only validates that each is non-empty.
  const hasDuplicateKeys = new Set(keys).size !== keys.length;
  const isValid =
    sections.length > 0 &&
    !hasDuplicateKeys &&
    sections.every((s) => s.key.trim() && s.title.trim() && s.body.trim());

  return (
    <section className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-foreground">
            Invoice terms {data?.version ? `(v${data.version})` : ''}
          </h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Snapshotted onto each invoice at creation, so editing here only affects new invoices.
          </p>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" className="h-[36px] shrink-0" onClick={addSection}>
            Add section
          </Button>
        )}
      </div>

      <div className="mt-4 space-y-4">
        {sections.map((s, i) => (
          <div key={i} className="rounded-[12px] border border-border p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[12px] font-medium text-muted-foreground">Key</span>
                <input
                  value={s.key}
                  disabled={!canManage}
                  onChange={(e) => setField(i, 'key', e.target.value)}
                  className="mt-1 w-full h-[36px] rounded-[8px] border border-border bg-background px-3 text-sm disabled:opacity-60"
                />
              </label>
              <label className="block">
                <span className="text-[12px] font-medium text-muted-foreground">Title</span>
                <input
                  value={s.title}
                  disabled={!canManage}
                  onChange={(e) => setField(i, 'title', e.target.value)}
                  className="mt-1 w-full h-[36px] rounded-[8px] border border-border bg-background px-3 text-sm disabled:opacity-60"
                />
              </label>
            </div>
            <label className="block mt-3">
              <span className="text-[12px] font-medium text-muted-foreground">Body</span>
              <textarea
                value={s.body}
                disabled={!canManage}
                rows={3}
                onChange={(e) => setField(i, 'body', e.target.value)}
                className="mt-1 w-full rounded-[8px] border border-border bg-background px-3 py-2 text-sm disabled:opacity-60"
              />
            </label>
            {canManage && sections.length > 1 && (
              <div className="mt-2 flex justify-end">
                <button
                  onClick={() => removeSection(i)}
                  className="text-xs text-red-600 hover:underline"
                >
                  Remove section
                </button>
              </div>
            )}
          </div>
        ))}
        {sections.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No terms sections configured.{canManage && ' Add at least one before saving.'}
          </p>
        )}
      </div>

      {canManage && (
        <div className="mt-5 flex items-center justify-end gap-3">
          {!isValid && (
            <span className="text-[12px] text-muted-foreground">
              {hasDuplicateKeys
                ? "Section keys must be unique."
                : "Every section needs a key, title and body."}
            </span>
          )}
          <Button
            className="h-[40px]"
            disabled={!isValid || update.isPending}
            loading={update.isPending}
            onClick={() => update.mutate({ sections })}
          >
            Save terms
          </Button>
        </div>
      )}
    </section>
  );
}

export function SalesConfigPanel({ canManage }: { canManage: boolean }) {
  return (
    <div className="space-y-6 max-w-4xl">
      <AssumptionsSection canManage={canManage} />
      <TermsSection canManage={canManage} />
    </div>
  );
}
